import { zodResolver } from "@hookform/resolvers/zod";
import {
  HttpError,
  useForgotPassword,
  useLogin,
  useTranslation,
  useUpdatePassword,
} from "@refinedev/core";
import { useForm } from "@refinedev/react-hook-form";
import axios from "axios";
import config from "app-config/config.json";
import Button from "components/button";
import { FullScreenLoading } from "components/fullScreenLoading";
import Input from "components/input";
import LanguageGroup from "components/wps/LanguageGroup";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Link, useLocation } from "react-router-dom";
import {
  REGEX_STRONG_PASSWORD,
  REGEX_VALID_EMAIL,
} from "utills/constants";
import { getAuthUserType } from "utills/authType";
import { z } from "zod";

import "routes/resetPassword/reset-password.scss";

const WORKSPACE_URL = config.WORKSPACE_API + `/api`;

type AuthMode = "login" | "forgot-password" | "reset-password";
type AuthType = ReturnType<typeof getAuthUserType>;
type AuthLanguage = "vi" | "en";

const AUTH_FORM_TEXT: Record<
  AuthLanguage,
  {
    confirmPasswordLabel: string;
    confirmPasswordNotMatch: string;
    email: string;
    forgotPasswordButton: string;
    forgotPasswordLink: string;
    forgotSuccess: (email: string) => string;
    invalidEmail: string;
    invalidResetContent: string;
    invalidResetTitle: string;
    newPassword: string;
    password: string;
    passwordResetSuccess: string;
    requiredEmail: string;
    requiredNewPassword: string;
    requiredPassword: string;
    signInLink: string;
    strongPassword: string;
    submit: string;
    title: Record<AuthMode, string>;
  }
> = {
  en: {
    confirmPasswordLabel: "Confirm New Password",
    confirmPasswordNotMatch: "Confirm password does not match",
    email: "Email",
    forgotPasswordButton: "Forgot Password",
    forgotPasswordLink: "Forgot password?",
    forgotSuccess: (email) =>
      `We sent an email to reset your password to the email ${email}. Please check your inbox`,
    invalidEmail: "Invalid email address",
    invalidResetContent: "This reset password link is invalid or expired.",
    invalidResetTitle: "Invalid reset password link",
    newPassword: "New Password",
    password: "Password",
    passwordResetSuccess: "Your password has been reset successfully",
    requiredEmail: "Please enter your email address",
    requiredNewPassword: "Please enter your new password",
    requiredPassword: "Please enter your password",
    signInLink: "Sign in",
    strongPassword:
      "Password must contain at least 8 characters, including uppercase, lowercase, number and special character",
    submit: "SUBMIT",
    title: {
      login: "Sign in to your account",
      "forgot-password": "Forgot Password",
      "reset-password": "RESET PASSWORD",
    },
  },
  vi: {
    confirmPasswordLabel: "Xác nhận mật khẩu mới",
    confirmPasswordNotMatch: "Xác nhận mật khẩu không khớp",
    email: "Email",
    forgotPasswordButton: "Quên mật khẩu",
    forgotPasswordLink: "Quên mật khẩu?",
    forgotSuccess: (email) =>
      `Chúng tôi đã gửi email đặt lại mật khẩu đến ${email}. Vui lòng kiểm tra hộp thư của bạn`,
    invalidEmail: "Email không hợp lệ",
    invalidResetContent:
      "Liên kết đặt lại mật khẩu này không hợp lệ hoặc đã hết hạn.",
    invalidResetTitle: "Liên kết đặt lại mật khẩu không hợp lệ",
    newPassword: "Mật khẩu mới",
    password: "Mật khẩu",
    passwordResetSuccess: "Mật khẩu của bạn đã được đặt lại thành công",
    requiredEmail: "Vui lòng nhập email",
    requiredNewPassword: "Vui lòng nhập mật khẩu mới",
    requiredPassword: "Vui lòng nhập mật khẩu",
    signInLink: "Đăng nhập",
    strongPassword:
      "Mật khẩu phải có ít nhất 8 ký tự, bao gồm chữ hoa, chữ thường, số và ký tự đặc biệt",
    submit: "XÁC NHẬN",
    title: {
      login: "Đăng nhập vào tài khoản",
      "forgot-password": "Quên mật khẩu",
      "reset-password": "ĐẶT LẠI MẬT KHẨU",
    },
  },
};

const isAuthLanguage = (value?: string | null): value is AuthLanguage => {
  return value === "vi" || value === "en";
};

const getInitialAuthLanguage = (locale?: string): AuthLanguage => {
  const storedLanguage = localStorage.getItem("lang");

  if (isAuthLanguage(storedLanguage)) {
    return storedLanguage;
  }

  if (isAuthLanguage(locale)) {
    return locale;
  }

  return "vi";
};

interface AuthFormProps {
  mode: AuthMode;
}

interface AuthFormVariables {
  email: string;
  password: string;
  confirmPassword: string;
}

interface ResetPasswordVariables {
  password?: string;
  confirmPassword?: string;
  token: string;
  user_id: string;
  type: AuthType;
}

interface ForgotPasswordVariables {
  email: string;
  type: AuthType;
}

export const AuthForm = ({ mode }: AuthFormProps) => {
  const location = useLocation();
  const { changeLocale, getLocale } = useTranslation();
  const [type, setType] = useState<AuthType>(getAuthUserType());
  const [language, setLanguage] = useState<AuthLanguage>(() =>
    getInitialAuthLanguage(getLocale())
  );
  const [id, setId] = useState("");
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(mode === "reset-password");
  const [linkValid, setLinkValid] = useState(true);
  const isWps = type === "wps";
  const activeLanguage = isWps ? language : "en";
  const text = AUTH_FORM_TEXT[activeLanguage];

  const schema = useMemo(() => {
    const emailSchema = z
      .string()
      .min(1, { message: text.requiredEmail })
      .refine(
        (value) => REGEX_VALID_EMAIL.test(value ?? ""),
        text.invalidEmail
      );

    if (mode === "login") {
      return z.object({
        email: emailSchema,
        password: z.string().min(1, { message: text.requiredPassword }),
      });
    }

    if (mode === "forgot-password") {
      return z.object({
        email: emailSchema,
      });
    }

    return z
      .object({
        password: z
          .string()
          .min(1, { message: text.requiredNewPassword })
          .refine(
            (value) => REGEX_STRONG_PASSWORD.test(value ?? ""),
            text.strongPassword
          ),
        confirmPassword: z.string(),
      })
      .superRefine(({ confirmPassword, password }, ctx) => {
        if (confirmPassword !== password) {
          ctx.addIssue({
            path: ["confirmPassword"],
            code: "custom",
            message: text.confirmPasswordNotMatch,
          });
        }
      });
  }, [mode, text]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AuthFormVariables, HttpError, AuthFormVariables>({
    resolver: zodResolver(schema),
  });

  const { mutate: login, isLoading: isLoggingIn } = useLogin();
  const { mutate: forgotPassword, isLoading: isForgettingPassword } = useForgotPassword<ForgotPasswordVariables>();
  const { mutate: updatePassword, isLoading: isUpdatingPassword } = useUpdatePassword<ResetPasswordVariables>();

  const isSubmitting =
    mode === "login"
      ? isLoggingIn
      : mode === "forgot-password"
        ? isForgettingPassword
        : isUpdatingPassword;

  useEffect(() => {
    const authType = getAuthUserType();

    setType(authType);

    if (authType === "wps") {
      const nextLanguage = getInitialAuthLanguage(getLocale());

      setLanguage(nextLanguage);
      localStorage.setItem("lang", nextLanguage);
      changeLocale(nextLanguage);
    }
  }, [changeLocale, getLocale]);

  useEffect(() => {
    if (mode !== "forgot-password") {
      return;
    }

    return () => {
      toast.dismiss();
    };
  }, [mode]);

  useEffect(() => {
    if (mode !== "reset-password") {
      return;
    }

    const params = new URLSearchParams(location.search);
    const userId = params.get("user_id") ?? "";
    const resetToken = params.get("token") ?? "";

    setId(userId);
    setToken(resetToken);

    if (!userId || !resetToken) {
      setLoading(false);
      setLinkValid(false);
      return;
    }

    if (type === "admin") {
      setLoading(false);
      setLinkValid(true);
    }
  }, [location.search, mode, type]);

  useEffect(() => {
    if (mode !== "reset-password" || type !== "wps" || !id || !token) {
      return;
    }

    const checkValidateLink = async () => {
      try {
        setLoading(true);

        const lang = localStorage.getItem("lang");
        const headers = {
          "accept-language": lang && lang !== "" ? lang : "vi",
        };

        await axios.get(
          `${WORKSPACE_URL}/auth/${id}/password/reset/verify?token=${encodeURIComponent(token)}`,
          { headers }
        );
        setLinkValid(true);
      } catch (error: any) {
        setLinkValid(false);
      } finally {
        setLoading(false);
      }
    };

    checkValidateLink();
  }, [id, language, mode, token, type]);

  const onSubmit = (values: AuthFormVariables) => {
    if (mode === "login") {
      return login({
        email: values.email,
        password: values.password,
        type,
      });
    }

    if (mode === "forgot-password") {
      return forgotPassword(
        { email: values.email, type },
        {
          onSuccess: (data) => {
            if (data.success) {
              toast.success(text.forgotSuccess(values.email), {
                duration: 60000,
                position: "top-center",
              });
            }
          },
        }
      );
    }

    return updatePassword(
      {
        password: values.password,
        confirmPassword: values.confirmPassword,
        token,
        user_id: id,
        type,
      },
      {
        onSuccess: (data) => {
          if (type === "wps" && data.success) {
            toast.success(text.passwordResetSuccess, {
              duration: 6000,
              position: "top-center",
            });
          }
        },
      }
    );
  };

  const submitLabel = mode === "login" ? text.signInLink.toUpperCase() : text.submit;

  const languageToggle = isWps ? (
    <div className="mt-5 flex justify-center">
      <LanguageGroup
        onLanguageChange={(nextLanguage) => setLanguage(nextLanguage)}
      />
    </div>
  ) : null;

  if (mode === "reset-password" && !loading && !linkValid) {
    return (
      <div className="link-invalid">
        <div className="absolute right-6 top-6">{languageToggle}</div>
        <img src="/images/icons/logo.png" className="logo-company" alt="logo" />

        <div className="link-invalid-title">{text.invalidResetTitle}</div>
        <div
          className="link-invalid-content"
          dangerouslySetInnerHTML={{
            __html: text.invalidResetContent,
          }}
        />

        <Link to="/forgot-password">
          <div className="btn-forgor-password">{text.forgotPasswordButton}</div>
        </Link>
      </div>
    );
  }

  return (
    <div className="sm:mx-auto sm:w-full sm:max-w-sm">
      {mode === "reset-password" && loading && <FullScreenLoading />}
      {languageToggle}

      <h2
        className="mt-8 text-center text-2xl font-bold leading-9 tracking-tight text-gray-900"
      >
        {text.title[mode]}
      </h2>

      <form onSubmit={handleSubmit(onSubmit)} className="mt-8">
        <fieldset disabled={isSubmitting} className="space-y-6">
          {(mode === "login" || mode === "forgot-password") && (
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                {text.email}
              </label>
              <div className="mt-2">
                <Input
                  label={text.email}
                  id="email"
                  type="text"
                  placeholder={text.email}
                  error={errors.email}
                  {...register("email")}
                />
                {errors.email ? (
                  <p className="error-text">
                    <span className="font-medium">{errors.email.message}</span>
                  </p>
                ) : null}
              </div>
            </div>
          )}

          {mode === "login" && (
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                {text.password}
              </label>
              <div className="mt-2">
                <Input
                  label={text.password}
                  id="password"
                  type="password"
                  error={errors.password}
                  {...register("password")}
                />
                {errors.password ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors.password.message}
                    </span>
                  </p>
                ) : null}
              </div>
            </div>
          )}

          {mode === "reset-password" && (
            <>
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  {text.newPassword}
                </label>
                <div className="mt-2">
                  <Input
                    label={text.newPassword}
                    id="password"
                    type="password"
                    error={errors.password}
                    {...register("password")}
                  />
                  {errors.password ? (
                    <p className="error-text">
                      <span className="font-medium">
                        {errors.password.message}
                      </span>
                    </p>
                  ) : null}
                </div>
              </div>

              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  {text.confirmPasswordLabel}
                </label>
                <div className="mt-2">
                  <Input
                    label={text.confirmPasswordLabel}
                    id="confirmPassword"
                    type="password"
                    error={errors.confirmPassword}
                    {...register("confirmPassword")}
                  />
                  {errors.confirmPassword ? (
                    <p className="error-text">
                      <span className="font-medium">
                        {errors.confirmPassword.message}
                      </span>
                    </p>
                  ) : null}
                </div>
              </div>
            </>
          )}

          <div className="flex flex-col items-center">
            <Button type="submit" loading={isSubmitting} className="w-full h-11">
              {submitLabel}
            </Button>

            {mode === "login" && (
              <div className="text-sm mt-2.5">
                <Link
                  to="/forgot-password"
                  className="font-semibold text-brand-500 hover:text-brand-400"
                >
                  {text.forgotPasswordLink}
                </Link>
              </div>
            )}

            {mode === "forgot-password" && (
              <div className="text-sm mt-2.5">
                <Link
                  to="/login"
                  className="font-semibold text-brand-500 hover:text-brand-400"
                >
                  {text.signInLink}
                </Link>
              </div>
            )}
          </div>
        </fieldset>
      </form>
    </div>
  );
};
